import type { Application, FederatedPointerEvent} from 'pixi.js';
import { Container, Graphics, Text } from 'pixi.js';
import { drawBevelPanel, drawInsetRect, GUGI_SHADOW } from '../rendering/PanelUtils';
import changelog from 'virtual:changelog';

const ACCENT     = 0xDD2233;
const ACCENT_DIM = 0x881122;
const PANEL_BG   = 0x0c0e14;
const INSET_BG   = 0x080a0f;

interface ChangelogEntry {
  date: string;
  message: string;
}

export class ChangelogOverlay {
  private root = new Container();
  private cleanup: (() => void) | null = null;

  constructor(private app: Application) {
    this.build();
    app.stage.addChild(this.root);
  }

  private close() {
    this.cleanup?.();
    this.cleanup = null;
    this.app.stage.removeChild(this.root);
    this.root.destroy({ children: true });
  }

  private build() {
    const { width: W, height: H } = this.app.screen;
    const panelW = Math.min(520, W - 24);
    const panelH = Math.min(H - 60, 500);
    const padX = Math.min(20, Math.round(panelW * 0.04));
    const headerH = 50;
    const listTop = headerH + 8;
    const listH = panelH - listTop - 16;

    // Backdrop
    const backdrop = new Graphics();
    backdrop.rect(0, 0, W, H);
    backdrop.fill({ color: 0x000000, alpha: 0.65 });
    backdrop.eventMode = 'static';
    backdrop.on('pointerdown', () => this.close());
    this.root.addChild(backdrop);

    // Panel
    const panel = new Container();
    const panelX = Math.round((W - panelW) / 2);
    const panelY = Math.round((H - panelH) / 2);
    panel.position.set(panelX, panelY);
    this.root.addChild(panel);

    // Background
    const bg = new Graphics();
    drawBevelPanel(bg, 0, 0, panelW, panelH, {
      fill: PANEL_BG, border: ACCENT_DIM, radius: 8, accentEdge: ACCENT, rivets: true,
    });
    bg.eventMode = 'static';
    bg.on('pointerdown', (e: FederatedPointerEvent) => e.stopPropagation());
    panel.addChild(bg);

    // Title
    const title = new Text({
      text: 'CHANGELOG',
      style: { fontFamily: 'Gugi', fontSize: 22, fill: ACCENT, dropShadow: GUGI_SHADOW },
    });
    title.anchor.set(0.5, 0);
    title.position.set(panelW / 2, 14);
    panel.addChild(title);

    // Close button
    const closeBtn = this.buildCloseButton();
    closeBtn.position.set(panelW - 44, 11);
    panel.addChild(closeBtn);

    // Inset area for the scrollable list
    const insetX = padX;
    const insetY = listTop;
    const insetW = panelW - padX * 2;
    const insetH = listH;

    const insetBg = new Graphics();
    drawInsetRect(insetBg, insetX, insetY, insetW, insetH, 4, INSET_BG, ACCENT_DIM, 0.3);
    panel.addChild(insetBg);

    // Scrollable content container with mask
    const maskGfx = new Graphics();
    maskGfx.rect(insetX + 1, insetY + 1, insetW - 2, insetH - 2);
    maskGfx.fill(0xffffff);
    panel.addChild(maskGfx);

    const scrollContainer = new Container();
    scrollContainer.mask = maskGfx;
    panel.addChild(scrollContainer);

    // Build entries
    const entries: ChangelogEntry[] = changelog;
    let ey = insetY + 6;
    const entryX = insetX + 10;
    const textW = insetW - 20;

    // Group entries by date (YYYY-MM-DD)
    let lastDateStr = '';
    for (const entry of entries) {
      const dateObj = new Date(entry.date);
      const dateStr = dateObj.toLocaleDateString('en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
      const timeStr = dateObj.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit',
      });

      // Date header when date changes
      if (dateStr !== lastDateStr) {
        if (lastDateStr !== '') ey += 6;
        const dateLabel = new Text({
          text: dateStr,
          style: { fontFamily: 'Gugi', fontSize: 12, fill: ACCENT_DIM },
        });
        dateLabel.position.set(entryX, ey);
        scrollContainer.addChild(dateLabel);
        ey += 20;
        lastDateStr = dateStr;
      }

      // Time + message
      const line = new Text({
        text: `${timeStr}  ${entry.message}`,
        style: {
          fontFamily: 'Offside', fontSize: 12, fill: 0x889aab,
          wordWrap: true, wordWrapWidth: textW - 40,
        },
      });
      line.position.set(entryX + 8, ey);
      scrollContainer.addChild(line);
      ey += line.height + 6;
    }

    const contentHeight = ey - insetY;
    const maxScroll = Math.max(0, contentHeight - insetH + 12);

    // Scroll handling
    let scrollY = 0;
    let scrollbar: Graphics | null = null;
    let drawScrollbar: (() => void) | null = null;

    const applyScroll = () => {
      scrollContainer.y = -scrollY;
      drawScrollbar?.();
    };

    const setScroll = (next: number) => {
      scrollY = Math.max(0, Math.min(maxScroll, next));
      applyScroll();
    };

    // Scrollbar (if content overflows)
    if (maxScroll > 0) {
      const barX = insetX + insetW - 8;
      const barTrackY = insetY + 4;
      const barTrackH = insetH - 8;
      const thumbRatio = insetH / contentHeight;
      const thumbH = Math.max(20, barTrackH * thumbRatio);

      scrollbar = new Graphics();
      panel.addChild(scrollbar);

      drawScrollbar = () => {
        const thumbY = barTrackY + (barTrackH - thumbH) * (maxScroll > 0 ? scrollY / maxScroll : 0);
        scrollbar!.clear();
        scrollbar!.roundRect(barX, barTrackY, 5, barTrackH, 2);
        scrollbar!.fill({ color: 0x1a1e28, alpha: 0.6 });
        scrollbar!.roundRect(barX, thumbY, 5, thumbH, 2);
        scrollbar!.fill({ color: ACCENT_DIM, alpha: 0.6 });
      };
      drawScrollbar();
    }

    // Mouse wheel scrolling — bind to canvas DOM for reliability
    const canvas = this.app.canvas;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScroll(scrollY + e.deltaY * 0.5);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });

    // Touch / pointer drag scrolling
    let dragging = false;
    let lastY = 0;
    let pointerId = -1;
    bg.on('pointerdown', (e: FederatedPointerEvent) => {
      dragging = true;
      pointerId = e.pointerId;
      lastY = e.global.y;
    });
    bg.on('pointermove', (e: FederatedPointerEvent) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dy = e.global.y - lastY;
      lastY = e.global.y;
      setScroll(scrollY - dy);
    });
    const endDrag = () => { dragging = false; pointerId = -1; };
    bg.on('pointerup', endDrag);
    bg.on('pointerupoutside', endDrag);
    bg.on('pointercancel', endDrag);

    this.cleanup = () => {
      canvas.removeEventListener('wheel', onWheel);
    };
  }

  private buildCloseButton(): Container {
    const c = new Container();
    const bg = new Graphics();
    bg.roundRect(0, 0, 32, 32, 4);
    bg.fill(0x1a1e28);
    bg.stroke({ color: ACCENT_DIM, width: 1 });
    c.addChild(bg);
    const label = new Text({ text: '×', style: { fontFamily: 'Offside', fontSize: 26, fill: 0x667788 } });
    label.anchor.set(0.5);
    label.position.set(16, 16);
    c.addChild(label);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointerdown', (e: FederatedPointerEvent) => { e.stopPropagation(); this.close(); });
    return c;
  }
}
